import React, { Component } from 'react';
import PropTypes from 'prop-types';
import url from 'url';
import classnames from 'classnames';
import pluralize from 'pluralize';
import queryString from 'query-string';
import moment from 'moment';
import { Swipeable, LEFT } from 'react-swipeable';
import lodashCompact from 'lodash/fp/compact';
import lodashGet from 'lodash/fp/get';
import { Redirect } from 'react-router-dom';

import isBoolean from 'common/util/isBoolean';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import {
  hasRoleAnonymous,
  hasRoleAgent,
  hasRoleApartmentLocator,
  hasRoleAdmin,
  hasRoleFieldOps,
  hasRoleLimitedTours,
  hasRoleRenter,
  hasRolePartner,
} from 'common/userRoles';
import typeCheck from 'common/util/typeCheck';
import {
  isRenterQualifiedForListing,
} from 'common/householdApplication/prequalificationHelpers';
import {
  isListingPreLease,
  getListingFieldsForDisplay,
  getListingUrlPath,
  makeApartmentUnitsFilter,
  getTourFromListing,
  getListingManagedPhotoUrls,
  showWarningMoveInDateBeforeHomeAvailable,
  shouldShowWarningMoveInDateTooFar,
  shouldShowWarningMoveInDatePast,
  getListingAvailableDateRange,
  existsPetData,
  getListingAmenities,
  isSelfShowingListing,
  hasListingPetWeightLimit,
  getOwnerPaysForFields,
  isListingTenantScreening,
  getListingApplyURL,
  isPreLeaseActive,
} from 'common/listingHelpers';
import { getListingContactPhone } from 'common/pageHelpers';
import {
  LEAD_ACTIVITY_BOOKMARK,
  LEAD_ACTIVITY_CHECK_AVAILABILITY,
} from 'common/leadConstants';
import {
  numberWithCommas,
  formatPhoneNumber,
} from 'common/formatters';
import {
  LISTING_STATUS_ACTIVE,
  LISTING_DATA_SOURCE_SUNROOM,
  LISTING_DATA_SOURCE_REALSAVVY,
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_DISPLAY_TYPE_SINGLE_UNIT,
  LISTING_DISPLAY_TYPE_MULTI_UNIT,
  LISTING_LOCKBOX_TYPE_LOCKBOX,
} from 'common/listingConstants';
import {
  SR_API_ACTION_GET_LISTING_BY_ID,
  SR_API_ACTION_GET_LISTING_LIST,
  SR_API_ACTION_TRACK_3D_TOUR_OPENED,
  SR_API_ACTION_TRACK_3D_TOUR_CLOSED,
  SR_API_ACTION_RENTLINX_LEAD_VISIT_LINK,
} from 'api/actionTypes';
import {
  TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING,
} from 'common/tourConstants';
import { PARTNER_BILLING_PLAN_FULL_SERVICE } from 'common/partnerConstants';
import {
  APPLICATION_FORM_TYPE_RENTER_PREQUAL,
} from 'common/householdApplication/applicationFormFields';
import {
  MEDIA_TYPE_3D_TOUR,
  MEDIA_TYPE_VIDEO,
} from 'common/mediaHelpers';
import {
  SCREEN_ID_LISTING,
} from 'client/constants/screenIds';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_LISTING_VIEWED,
  ANALYTICS_EVENT_LISTING_SHARE_CLICKED,
  ANALYTICS_EVENT_CONTACT_BUTTON_CLICKED,
  ANALYTICS_EVENT_CALL_BUTTON_CLICKED,
  ANALYTICS_EVENT_TEXT_BUTTON_CLICKED,
  ANALYTICS_EVENT_PARTNER_CALLED,
} from 'client/analytics/eventList';
import {
  makeListingApplicationContext,
} from 'client/analytics/makeApplicationContext';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';

import Navbar from 'client/components/Navbar';
import SrNavbarToast, {
  SR_NAVBAR_TOAST_THEME_YELLOW,
} from 'client/components/SrNavbarToast';
import SrNavbarToastButton from 'client/components/SrNavbarToastButton';
import ActivityIndicator from 'client/components/ActivityIndicator';
import SrListingTourSection from 'client/components/SrListingTourSection';
import SrListingSelfTouringSection from 'client/components/SrListingSelfTouringSection';
import SrListingTourAgentSection from 'client/components/SrListingTourAgentSection';
import SrOpenStreetMap from 'client/components/SrOpenStreetMap';
import SrContactModal from 'client/components/SrContactModal';
import SrAskQuestionModal from 'client/components/SrAskQuestionModal';
import NavbarButton from 'client/components/NavbarButton';
import SrListingBackButton from 'client/components/SrListingBackButton';
import SrApartmentUnitRow from 'client/components/SrApartmentUnitRow';
import SrLink from 'client/components/SrLink/SrLink';
import SrActivityBanner from 'client/components/SrActivityBanner';
import SrListingAddToTourSection from 'client/components/SrListingAddToTourSection';
import SrRecommendedListings from 'client/components/SrRecommendedListings';
import SrListingMoreUnits from 'client/components/SrListingMoreUnits';
import SrHowDoILeaseListingSection from 'client/components/SrHowDoILeaseListingSection';
import SrListingPrequal from 'client/components/SrListingPrequal';
import SrListingMoveInWindow from 'client/components/SrListingMoveInWindow';
import SrApplicationButton from 'client/components/SrApplicationButton';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrWhoCoversTheBillsInfoBlock from 'client/components/SrWhoCoversTheBillsInfoBlock';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW,
  BUTTON_THEME_WHITE,
  BUTTON_THEME_BLUE,
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrApartmentSpecialDealsList from 'client/components/SrApartmentSpecialDealsList';
import SrListingAdminList from 'client/components/SrListingAdminList';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrLinkCard from 'client/components/SrLinkCard';
import SrListingPriceBlock from 'client/components/SrListingPriceBlock';
import SrFeatureCard from 'client/components/SrFeatureCard';
import SrListingExpensesCard from 'client/components/SrListingExpensesCard';
import SrTooltip from 'client/components/SrTooltip';
import SrIcon from 'client/components/SrIcon';
import SrPropertyFeaturesBlock from 'client/components/SrPropertyFeaturesBlock';
import SrMediaCarousel from 'client/components/SrMediaCarousel';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrListingJoinWaitlist from 'client/components/SrListingJoinWaitlist';
import SrConcessionsBanner from 'client/components/SrConcessionsBanner/SrConcessionsBanner';

import postWithState from 'client/actions/postWithState';
import PreLeaseActions from 'client/actions/PreLease';
import ListingActions from 'client/actions/Listing';
import UserActions from 'client/actions/User';
import AgentActions from 'client/actions/Agent';
import TourActions from 'client/actions/Tour';
import HouseholdActions from 'client/actions/Household';
import ApartmentActions from 'client/actions/Apartment';
import ApartmentLocatorActions from 'client/actions/ApartmentLocator';
import MarketActions from 'client/actions/Market';
import PartnerActions from 'client/actions/Partner';
import PartnerUserActions from 'client/actions/PartnerUser';
import SunlockActions from 'client/actions/Sunlock';
import LeaseActions from 'client/actions/Lease';
import FloorPlanActions from 'client/actions/FloorPlan';
import MediaActions from 'client/actions/Media';
import NavigatorActions from 'client/actions/Navigator';
import TenantScreeningCriteriaActions from 'client/actions/TenantScreeningCriteria';
import BuyersSideTourRequestActions from 'client/actions/BuyersSideTourRequest';
import branch from 'client/core/branch';
import withLocationQuery from 'client/helpers/withLocationQuery';
import ConcessionActions from 'client/actions/Concession';
import { getLatestActiveConcession } from 'common/concessionHelpers';

function filterApartmentUnits({ apartmentUnits, searchFilters }) {
  typeCheck('apartmentUnits::Array', apartmentUnits);
  typeCheck('searchFilters::Maybe Object', searchFilters);

  if (!searchFilters) {
    return apartmentUnits;
  }
  const apartmentUnitsFilter = makeApartmentUnitsFilter(searchFilters);
  const filteredApartmentUnits = apartmentUnits.filter(apartmentUnitsFilter);
  return filteredApartmentUnits;
}

const WARNING_MESSAGE_TYPE_MOVE_IN_DATE_IN_PAST = 'WARNING_MESSAGE_TYPE_MOVE_IN_DATE_IN_PAST';
const WARNING_MESSAGE_TYPE_MOVE_IN_DATE_TOO_FAR = 'WARNING_MESSAGE_TYPE_MOVE_IN_DATE_TOO_FAR';
const WARNING_MESSAGE_TYPE_MOVE_IN_DATE_BEFORE_AVAILABLE =
  'WARNING_MESSAGE_TYPE_MOVE_IN_DATE_BEFORE_AVAILABLE';

@withLocationQuery
@withTransitionHook
@withScreenId(SCREEN_ID_LISTING)
@withScrollToTop
@withApiState({
  listingApiState: {
    action: SR_API_ACTION_GET_LISTING_BY_ID,
  },
  recommendedListingApiState: {
    action: SR_API_ACTION_GET_LISTING_LIST,
  },
})
@branch({
  recommendedListings: ['recommendedListings'],
  isRecommendedListingsFullyLoaded: ['recommendedListings', 'isFullyLoaded'],
  listing: ['listing'],
  listingShowingInfo: ['agent', 'listingShowingInfo'],
  currentUser: ['currentUser'],
  assignedApartmentLocator: ['assignedApartmentLocator'],
  tours: ['tours'],
  bookmarksByListingId: ['bookmarksByListingId'],
  viewingListing: ['agentMapView', 'viewingListing'],
  appUrl: ['env', 'APP_URL'],
  listingPhotoResolution: ['listingPhotoResolution'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  marketsById: ['markets', 'byId'],
  tenantScreeningCriteriaByPartner: ['tenantScreeningCriteria', 'byPartnerManagementCompany'],
  tenantScreeningCriteriaByListingId: ['tenantScreeningCriteria', 'byListingId'],
  householdsApplyingToListingByListingId: ['householdsApplyingToListingByListingId'],
  marketingListingsByListingId: ['partners', 'marketingListingsByListingId'],
  partnersById: ['partners', 'byId'],
  leasesByListingId: ['leases', 'byListingId'],
  listingHowDoILeaseSectionV1: [
    'analytics',
    'experiments',
    'activeVariants',
    'listingHowDoILeaseSectionV1',
  ],
  specialDealsByApartmentBuildingId: ['apartments', 'specialDealsByApartmentBuildingId'],
  listingLinksByListingId: ['listingLinksByListingId'],
  sunlocksById: ['sunlocks', 'byId'],
  apartmentsByBuildingId: ['apartments', 'byBuildingId'],
  listingAdminInfoById: ['listings', 'adminInfoById'],
  getRedirectionByListingId: ['listings', 'redirectionById'],
  propertyFeaturesByListingId: ['listings', 'propertyFeaturesById'],
  partnerUserListByPartnerId: ['partners', 'users', 'listByPartnerId'],
  showUnitsByFloorPlanId: ['floorPlans', 'showUnitsByFloorPlanId'],
  mediaByListingId: ['media', 'byListingId'],
  buyersSideTourRequests: ['buyersSideTourRequests', 'byListingAndUserId'],
  currentPartner: ['partners', 'currentPartner'],
})
@branch(({ match }) => ({
  preLease: ['preLeases', 'byListingId', match.params.listingId],
  concessions: ['concessions', 'byListingId', match.params.listingId],
}))
class ListingPage extends Component {
  static fetchData(match, inject) {
    const currentUser = inject.state.get(['currentUser']);
    function onLoadListing() {
      const listing = inject.state.get(['listing']);
      if (!listing) {
        return;
      }
      const promisesToResolve = [];

      promisesToResolve.push(MediaActions.getHierarchicalMedia(inject, {
        listingId: listing.id,
      }));

      if (listing.marketId) {
        promisesToResolve.push(MarketActions.getById(inject, { marketId: listing.marketId }));
      }

      if (listing.displayType === LISTING_DISPLAY_TYPE_SINGLE_UNIT) {
        promisesToResolve.push(ListingActions.getUnits(inject, { listing }));
      }

      if (listing.dataSource === LISTING_DATA_SOURCE_SUNROOM) {
        promisesToResolve.push(ListingActions.getHouseholdsApplyingToListing(inject, {
          listingId: listing.id,
        }));

        promisesToResolve.push(ListingActions.getPropertyFeatures(inject, {
          listingId: listing.id,
        }));
      }

      if (listing.apartmentBuildingId) {
        promisesToResolve.push(ApartmentActions.getApartmentSpecialDealsByApartmentBuildingId(inject, {
          apartmentBuildingId: listing.apartmentBuildingId,
        }));
      }

      if (listing.dataSource === LISTING_DATA_SOURCE_SUNROOM) {
        promisesToResolve.push(TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
          listingId: listing.id,
        }));

        if (listing.partnerId) {
          promisesToResolve.push(TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
            managementCompanyName: listing.listingManagementCompany,
          }));

          promisesToResolve.push(PartnerActions.getById(inject, {
            partnerId: listing.partnerId,
          }));

          promisesToResolve.push(PartnerActions.getMarketingListingByListingId(inject, {
            listingId: listing.id,
          }));
        }
      }

      if (listing.apartmentBuildingId) {
        promisesToResolve.push(ApartmentActions.getByApartmentBuildingId(inject, {
          apartmentBuildingId: listing.apartmentBuildingId,
        }));
      }

      if (listing.floorPlanId) {
        promisesToResolve.push(FloorPlanActions.getShowUnit(inject, {
          floorPlanId: listing.floorPlanId,
        }));
      }

      if (isListingPreLease(listing)) {
        promisesToResolve.push(PreLeaseActions.getByListingId(inject, {
          listingId: listing.id,
        }));
      }

      if (
        hasRoleAdmin(currentUser.roles)
        || hasRoleAgent(currentUser.roles)
        || hasRoleFieldOps(currentUser.roles)
      ) {
        promisesToResolve.push(ListingActions.getLinks(inject, { listingId: listing.id }));
        promisesToResolve.push(ListingActions.getRedirectionByListingId(inject, { listingId: listing.id }));
        promisesToResolve.push(LeaseActions.getByListingId(inject, { listingId: listing.id }));

        if (listing.sunlockId) {
          promisesToResolve.push(SunlockActions.getById(inject, { sunlockId: listing.sunlockId }));
        }
      }

      if (hasRoleRenter(currentUser.roles)) {
        promisesToResolve.push(
          BuyersSideTourRequestActions.getByListingId(inject, { listingId: listing.id })
        );
      }

      return Promise.all(promisesToResolve);
    }

    if (hasRoleAgent(currentUser.roles)) {
      return Promise.all([
        ListingActions.getById(inject, parseInt(match.params.listingId, 10)),
        AgentActions.getAgentTours(inject, { agentUserId: parseInt(currentUser.id, 10) }),
        AgentActions.getListingShowingInfo(inject, parseInt(match.params.listingId, 10)),
        UserActions.getApartmentLocator(inject, currentUser.id),
      ]).then(onLoadListing);
    }
    return Promise.all(lodashCompact([
      ListingActions.getById(inject, parseInt(match.params.listingId, 10)),
      UserActions.getCustomerSupportPhoneNumber(inject, { userId: currentUser.id }),
      ConcessionActions.getByListingId(inject, {
        listingId: parseInt(match.params.listingId, 10),
        forceReload: true,
      }),
      currentUser.id && UserActions.getApartmentLocator(inject, currentUser.id),
    ])).then(onLoadListing);
  }

  static getPageMetadata = (state) => {
    const listing = state.get('listing');
    const APP_URL = state.get(['env', 'APP_URL']);

    if (!listing) {
      return {};
    }

    const listingFieldsForDisplay = getListingFieldsForDisplay({ listing });
    const managedPhotoUrls = getListingManagedPhotoUrls({ listing });

    return {
      pageTitle: listing.displayType === LISTING_DISPLAY_TYPE_MULTI_UNIT
        ? (
          'Sunroom Rentals'
        ) : (
          listing.addressStreetDisplay + ', ' +
          listing.addressCity + ', ' +
          listing.addressStateCode + ' ' +
          listing.addressZip + ' | Sunroom Rentals'
        ),
      section: 'Listing',
      canonical: url.resolve(APP_URL, getListingUrlPath(listing)),
      keywords: (
        'rent, home, apartment, austin, texas, tour, ' +
        lodashCompact([listing.addressZip, listing.addressCity, listing.addressStreetDisplay]).join(', ')
      ).toLowerCase(),
      description: listing.displayType === LISTING_DISPLAY_TYPE_MULTI_UNIT
        ? (
          'Sunroom has ' + managedPhotoUrls.length +
          ' photos of this $' + numberWithCommas(listingFieldsForDisplay.price) + ' ' +
          'apartment with many floorplans at ' + listing.addressStreetDisplay +
          (listing.apartmentName ? ' - ' + listing.apartmentName + ' -' : '') +
          ' available for move in ' + moment.utc(listing.availableDate).format('MMMM Do, YYYY')
        )
        : (
          'Sunroom has ' + managedPhotoUrls.length +
          ' photos of this $' + numberWithCommas(listingFieldsForDisplay.price) + ' ' +
          'home with ' + listing.bedrooms + ' beds ' +
          listing.bathrooms + ' baths, ' + listing.squareFeet + ' sqft at ' +
          listing.addressStreetDisplay + ' available for move in ' +
          moment.utc(listing.availableDate).format('MMMM Do, YYYY')
        ),
      image: managedPhotoUrls[0],
    };
  }

  static propTypes = {
    appUrl: PropTypes.string.isRequired,
    listing: PropTypes.object,
    listingShowingInfo: PropTypes.object,
    listingApiState: PropTypes.object.isRequired,
    recommendedListingApiState: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    assignedApartmentLocator: PropTypes.object,
    tours: PropTypes.array,
    bookmarksByListingId: PropTypes.object,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    marketsById: PropTypes.object.isRequired,
    tenantScreeningCriteriaByPartner: PropTypes.object.isRequired,
    tenantScreeningCriteriaByListingId: PropTypes.object.isRequired,
    marketingListingsByListingId: PropTypes.object.isRequired,
    partnersById: PropTypes.object.isRequired,
    householdsApplyingToListingByListingId: PropTypes.object.isRequired,
    listingHowDoILeaseSectionV1: PropTypes.string.isRequired,
    specialDealsByApartmentBuildingId: PropTypes.object.isRequired,
    listingLinksByListingId: PropTypes.object.isRequired,
    leasesByListingId: PropTypes.object.isRequired,
    sunlocksById: PropTypes.object.isRequired,
    apartmentsByBuildingId: PropTypes.object.isRequired,
    listingAdminInfoById: PropTypes.object.isRequired,
    getRedirectionByListingId: PropTypes.object.isRequired,
    propertyFeaturesByListingId: PropTypes.object.isRequired,
    partnerUserListByPartnerId: PropTypes.object.isRequired,
    showUnitsByFloorPlanId: PropTypes.object.isRequired,
    mediaByListingId: PropTypes.object.isRequired,
    buyersSideTourRequests: PropTypes.object.isRequired,
    preLease: PropTypes.object,
    concessions: PropTypes.array,
  }

  constructor(props) {
    super(props);

    this.state = {
      showContactModal: false,
      showAskQuestionModal: false,
      // If the listing is a SmartApartment listing, but the filtered apartment units array is empty, start with all apartment units expanded.
      viewAllApartmentUnits: (
        !props.listing || props.listing.displayType !== LISTING_DISPLAY_TYPE_MULTI_UNIT
          ? false
          : (
            filterApartmentUnits({
              apartmentUnits: props.listing.apartmentUnits,
              searchFilters: props.currentUser.searchFilters,
            }).length === 0
          )
      ),
      showBookmarkToast: false,
      showBreedRestrictionsModal: false,
      showPartnerTourRequestNewModal: false,
      showPartnerTourRequestFollowUpModal: false,
    };

    this.tourAgentSectionRef = null;
  }

  componentDidMount() {
    const {
      dispatch,
      match,
      location,
      listing,
    } = this.props;

    if (listing) {
      this._loadMoreListingPageData(listing);
    }

    const listingId = parseInt(match.params.listingId, 10);
    dispatch(UserActions.updateViewedListings, {
      viewedListingId: listingId,
    });

    const queryUrl = queryString.parse(location.search);
    const activityId = queryUrl.activity_id ? parseInt(queryUrl.activity_id, 10) : null;
    if (activityId) {
      dispatch(ApartmentLocatorActions.markSeenActivity, {
        activityId: activityId,
      });
    }

    const rentlinxListingLeadId = parseInt(queryUrl.rentlinxListingLeadId, 10) || null;
    if (rentlinxListingLeadId) {
      dispatch(postWithState, {
        action: SR_API_ACTION_RENTLINX_LEAD_VISIT_LINK,
        payload: {
          rentlinxListingLeadId,
        },
      });
    }

    if (queryUrl.addBookmark) {
      this._addBookmark();
    }

    if (queryUrl.newPartnerTourRequestId) {
      this.setState({
        showPartnerTourRequestNewModal: true,
      });
      dispatch(NavigatorActions.replace, { to: `/listing/${listing.id}` });
    }
    else if (queryUrl.partnerTourRequestId && queryUrl.reachedOutByPartner) {
      this.setState({
        showPartnerTourRequestFollowUpModal: true,
      });
      dispatch(PartnerActions.partnerTourRequestFollowUp, {
        partnerTourRequestId: parseInt(queryUrl.partnerTourRequestId, 10),
        reachedOutByPartner: queryUrl.reachedOutByPartner === 'true',
      });
      dispatch(NavigatorActions.replace, { to: `/listing/${listing.id}` });
    }

    analytics.track(ANALYTICS_EVENT_LISTING_VIEWED);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.listing
      && nextProps.isSmartApartmentData
      && (!this.props.listing || this.props.listing.id !== nextProps.listing.id)
      && (
        filterApartmentUnits({
          apartmentUnits: nextProps.listing.apartmentUnits,
          searchFilters: nextProps.currentUser.searchFilters,
        }).length === 0
      )
    ) {
      this.setState({
        viewAllApartmentUnits: true,
      });
    }

    if (
      nextProps.listing
      && (!this.props.listing || this.props.listing.id !== nextProps.listing.id)
    ) {
      this._loadMoreListingPageData(nextProps.listing);
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(({ state }) => {
      state.set(['listing'], null);
      state.commit();
    });
  }

  _loadMoreListingPageData = (listing) => {
    const { partnersById, dispatch, currentUser } = this.props;
    const partner = partnersById[listing.partnerId];

    if (
      hasRoleAdmin(currentUser.roles)
      || hasRoleAgent(currentUser.roles)
      || hasRoleFieldOps(currentUser.roles)
    ) {
      dispatch(ListingActions.getAdminInfo, {
        listingId: listing.id,
      });

      if (partner) {
        dispatch(PartnerUserActions.getPartnerUserListByPartnerId, {
          partnerId: partner.id,
        });
      }
    }

    if (listing.displayType === LISTING_DISPLAY_TYPE_SINGLE_UNIT) {
      dispatch(TourActions.getAvailableTourDates);
      dispatch(TourActions.getUnavailableTourDates);
    }

    dispatch(ListingActions.getRecommendedList, {
      count: 3,
      filters: {
        addressZip: listing.addressZip,
        viewedListingIds: [listing.id],
      },
    });
  }

  _openUpdateMoveInDateModal = () => {
    const { dispatch } = this.props;
    dispatch(UserActions.setUpdateMoveInDateModalOpen, true);
  }

  _onPhoneClicked = (e) => {
    const { listing } = this.props;
    e.preventDefault();
    analytics.track(ANALYTICS_EVENT_CONTACT_BUTTON_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
    this.setState({
      showContactModal: true,
    });
  }

  _onCloseContactModal = () => {
    this.setState({
      showContactModal: false,
    });
  }

  _onClickMessage = () => {
    const { dispatch, listing } = this.props;
    analytics.track(ANALYTICS_EVENT_TEXT_BUTTON_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
    dispatch(ListingActions.askAQuestionButtonClick, listing.id);
  }

  _onClickCall = () => {
    const { dispatch, listing } = this.props;
    analytics.track(ANALYTICS_EVENT_CALL_BUTTON_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
    dispatch(ListingActions.callButtonClick, listing.id);
  }

  _onClickApartmentPhoneCall = () => {
    const { listing } = this.props;
    analytics.track(ANALYTICS_EVENT_PARTNER_CALLED, {
      ...makeListingApplicationContext(listing),
    });
    document.location.href = `tel:+1${listing.apartmentPhone}`;
  }

  _openAskQuestionModal = () => {
    this.setState({
      showAskQuestionModal: true,
    });
  }

  _onCloseAskQuestionModal = () => {
    this.setState({
      showAskQuestionModal: false,
    });
  }

  _onBookmarkClick = (event) => {
    const {
      bookmarksByListingId,
      listing,
      currentUser,
      dispatch,
      history,
    } = this.props;

    event.preventDefault();
    event.stopPropagation();

    if (hasRoleAnonymous(currentUser.roles)) {
      history.push(`/login?bookmarkRequested=${listing.id}`);
    }
    else if (bookmarksByListingId && bookmarksByListingId[listing.id]) {
      dispatch(UserActions.removeBookmark, { listingId: listing.id });
    }
    else {
      dispatch(UserActions.addBookmark, { listingId: listing.id });
    }
  }

  _onViewAllApartmentUnitsClick = () => {
    this.setState({
      viewAllApartmentUnits: true,
    });
  }

  _onTourAgentSectionRef = (tourAgentSectionRef) => {
    const { location, currentUser } = this.props;
    if (!hasRoleAgent(currentUser.roles)) {
      return;
    }

    this.tourAgentSectionRef = tourAgentSectionRef;
    if (location.search.includes('scrollTo=tourQuestions')) {
      this._goToTourAgentSection();
    }
  }

  _goToTourAgentSection = () => {
    if (!this.tourAgentSectionRef) {
      return;
    }

    this.tourAgentSectionRef.scrollIntoView({ behavior: 'smooth' });
  }

  _onListingPrequalSectionRef = (listingPrequalSectionRef) => {
    const { location } = this.props;

    if (location.search.includes('scrollTo=yourQualifications')) {
      setTimeout(() => {
        if (listingPrequalSectionRef) {
          listingPrequalSectionRef.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }, 500);
    }
  }

  _onScheduleTourSectionRef = (scheduleTourSectionRef) => {
    const { location } = this.props;

    if (location.search.includes('scrollTo=scheduleTour')) {
      if (scheduleTourSectionRef) {
        setTimeout(() => {
          scheduleTourSectionRef.scrollIntoView({
            behavior: 'smooth',
          });
        }, 500);
      }
    }
  }

  _goToScheduleTourSection = () => {
    const element = document.getElementById('scrollHereForScheduleTour');
    element.scrollIntoView({ behavior: 'smooth' });
  }

  _goToPrequalSection = () => {
    const element = document.getElementById('scrollHereForPrequal');
    element.scrollIntoView({ behavior: 'smooth' });
  }

  _addBookmark = () => {
    const {
      listing,
      dispatch,
    } = this.props;

    dispatch(UserActions.addBookmark, { listingId: listing.id })
      .then(() => {
        this.setState({
          showBookmarkToast: true,
        });
        setTimeout(() => {
          if (!this._unmounting) {
            this.setState({
              showBookmarkToast: false,
            });
          }
        }, 2000);
      });
  }

  _onEditPrequalButtonClick = () => {
    const { dispatch, listing } = this.props;

    dispatch(HouseholdActions.redirectToApplicationForm, {
      formType: APPLICATION_FORM_TYPE_RENTER_PREQUAL,
      prequalListingId: parseInt(listing.id, 10),
      shouldRestart: true,
    });
  }

  _onSwipedPhoto = ({ dir }) => {
    if (dir === LEFT) {
      this._onClickPhoto();
    }
  }

  _onClickPhoto = () => {
    const { listing, location, history } = this.props;
    const queryUrl = queryString.parse(location.search);
    const requestedFrom = queryUrl.requestedFrom
      ? queryUrl.requestedFrom
      : TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING;

    const listingPhotosUrl = requestedFrom !== TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING
      ? `/listing/${listing.id}/photos?requestedFrom=${requestedFrom}`
      : `/listing/${listing.id}/photos?`;

    history.push(listingPhotosUrl);
  }

  _onClickShareButtonLink = () => {
    const { listing } = this.props;
    analytics.track(ANALYTICS_EVENT_LISTING_SHARE_CLICKED, {
      ...makeListingApplicationContext(listing),
    });
  }

  _onToggleBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: !this.state.showBreedRestrictionsModal,
    });
  }

  _onClosePartnerTourRequestNewModal = () => {
    this.setState({
      showPartnerTourRequestNewModal: false,
    });
  }

  _onClosePartnerTourRequestSurveyModal = () => {
    this.setState({
      showPartnerTourRequestFollowUpModal: false,
    });
  }

  _on3dTourOpen = (media) => {
    const { dispatch, listing } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_TRACK_3D_TOUR_OPENED,
      payload: {
        viewingListingId: listing.id,
        mediaId: media.id,
      },
    });
  }

  _on3dTourClose = (media) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_TRACK_3D_TOUR_CLOSED,
      payload: {
        mediaId: media.id,
      },
    });
  }

  renderListing() {
    const {
      currentUser,
      assignedApartmentLocator,
      listing,
      recommendedListingApiState,
      recommendedListings: {
        records: recommenedListingsRecords,
        isRecommendedListingsFullyLoaded,
      },
      listingPhotoResolution,
      listingShowingInfo,
      tours,
      history,
      bookmarksByListingId,
      viewingListing,
      location,
      renterProfilesByUserId,
      dispatch,
      isTransitioning,
      marketsById,
      appUrl,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      marketingListingsByListingId,
      partnersById,
      listingHowDoILeaseSectionV1,
      listingLinksByListingId,
      leasesByListingId,
      sunlocksById,
      householdsApplyingToListingByListingId,
      specialDealsByApartmentBuildingId,
      apartmentsByBuildingId,
      listingAdminInfoById,
      getRedirectionByListingId,
      propertyFeaturesByListingId,
      partnerUserListByPartnerId,
      showUnitsByFloorPlanId,
      mediaByListingId,
      buyersSideTourRequests,
      preLease,
      concessions,
    } = this.props;

    const {
      viewAllApartmentUnits,
      showContactModal,
      showAskQuestionModal,
      showBreedRestrictionsModal,
      showPartnerTourRequestNewModal,
      showPartnerTourRequestFollowUpModal,
    } = this.state;

    // [PD-2292] Disabled temporarily until RealSavvy fixes their API.
    const shouldDisableRealSavvyFields = listing.dataSource === LISTING_DATA_SOURCE_REALSAVVY;

    const managedPhotoUrls = getListingManagedPhotoUrls({ listing });
    const mediaThumbnailUrl = managedPhotoUrls.slice(0, 5).pop();
    const media = (mediaByListingId[listing.id] || [])
      .map((media) => ({
        ...media,
        thumbnailUrl: mediaThumbnailUrl,
      }));
    const mediaVideos = media
      .filter(({ type }) => type === MEDIA_TYPE_VIDEO);
    const media3DTours = media
      .filter(({ type }) => type === MEDIA_TYPE_3D_TOUR)
      .map((media) => ({
        ...media,
        title: 'View 3D Tour',
      }));

    const disabledSettings = lodashGet(`${listing.marketId}.settings.listing.disabled`, marketsById) || [];
    const isAgent = hasRoleAgent(currentUser.roles);
    const isAdmin = hasRoleAdmin(currentUser.roles);
    const isLimitedTours = hasRoleLimitedTours(currentUser.roles);
    const isSelfShowing = isSelfShowingListing(listing);
    const renterProfile = renterProfilesByUserId && renterProfilesByUserId[currentUser.id] || null;
    const tenantScreeningCriteria = (
      tenantScreeningCriteriaByListingId[listing.id]
      || tenantScreeningCriteriaByPartner[listing.listingManagementCompany]
    );
    const showMoveInWindowSection = !isAgent && !adminTourId && !!tenantScreeningCriteria;
    const apartmentSpecialDeals = specialDealsByApartmentBuildingId[listing.apartmentBuildingId];
    const propertyFeatures = propertyFeaturesByListingId[listing.id];

    const queryUrl = queryString.parse(location.search);
    const agentTourId = queryUrl.agentTourId ? parseInt(queryUrl.agentTourId, 10) : null;
    const adminTourId = queryUrl.adminTourId ? parseInt(queryUrl.adminTourId, 10) : null;
    const requestedFrom = queryUrl.requestedFrom
      ? queryUrl.requestedFrom
      : TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING;

    const isBookmarked = bookmarksByListingId ? !!bookmarksByListingId[listing.id] : false;
    const listingTour = getTourFromListing({
      listingId: listing.id,
      tours: tours,
    });

    let listingAmenities = getListingAmenities(listing);
    const apartment = apartmentsByBuildingId[listing.apartmentBuildingId] || null;
    const partner = (listing.partnerId
      && partnersById[listing.partnerId]) || null;
    const market = listing.marketId && marketsById[listing.marketId] || null;
    const showUnit = listing.floorPlanId && showUnitsByFloorPlanId[listing.floorPlanId] || null;

    // (@d-oliveros)[PD-1825]: We don't have pets info for Dallas RealSavvy listings. Enabling the pets amenities here.
    if (market && market.displayName === 'Dallas') {
      listingAmenities = listingAmenities.map((amenity) => {
        switch (amenity.name) {
          case 'petsAllowed': {
            return { ...amenity, value: true };
          }
          case 'noPets': {
            return { ...amenity, value: false };
          }
          default:
            return amenity;
        }
      });
    }

    const activeListingAmenities = listingAmenities.filter(({ value, iconClassName, emoji }) => {
      return value && (iconClassName || emoji);
    });

    const petDescription = listing.petDescription || '';
    let petWeightLimit = listing.petWeight || null;

    if (petDescription.includes('Weight Limit - Extra Large')) {
      petWeightLimit = 75;
    }
    else if (petDescription.includes('Weight Limit - Large')) {
      petWeightLimit = 50;
    }
    else if (petDescription.includes('Weight Limit - Medium')) {
      petWeightLimit = 35;
    }
    else if (petDescription.includes('Weight Limit - Small')) {
      petWeightLimit = 20;
    }

    const shouldShowSchoolInformation = (
      listing.schoolDistrict
      || listing.schoolElementary
      || listing.schoolMiddle
      || listing.schoolSeniorHigh
    );

    const isSmartApartmentData = listing.displayType === LISTING_DISPLAY_TYPE_MULTI_UNIT;
    const isRealSavvy = listing.dataSource === LISTING_DATA_SOURCE_REALSAVVY;
    const isSunroomListing = listing.dataSource === LISTING_DATA_SOURCE_SUNROOM;

    let leaseLengthLabel = 'lease length';
    let leaseLengthValue = listing.maxLeaseMonths && listing.minLeaseMonths
      ? `${listing.minLeaseMonths} - ${listing.maxLeaseMonths} months`
      : '--';
    if (!listing.minLeaseMonths && listing.maxLeaseMonths) {
      leaseLengthLabel = 'lease length max.';
      leaseLengthValue = `${listing.maxLeaseMonths} months`;
    }
    else if (!listing.maxLeaseMonths && listing.minLeaseMonths) {
      leaseLengthLabel = 'lease length min.';
      leaseLengthValue = `${listing.minLeaseMonths} months`;
    }
    else if (listing.minLeaseMonths === listing.maxLeaseMonths) {
      leaseLengthValue = `${listing.minLeaseMonths} months`;
    }

    let showWarningMessage = false;
    let warningMessageType = null;

    if (
      !hasRoleAnonymous(currentUser.roles)
      && !hasRoleAgent(currentUser.roles)
    ) {
      if (shouldShowWarningMoveInDatePast({
        moveInDate: currentUser.searchFilters.moveInDate,
        listingAvailableDate: listing.availableDate,
      })) {
        showWarningMessage = true;
        warningMessageType = WARNING_MESSAGE_TYPE_MOVE_IN_DATE_IN_PAST;
      }
      else if (shouldShowWarningMoveInDateTooFar({
        moveInDate: currentUser.searchFilters.moveInDate,
        listingAvailableDate: listing.availableDate,
      })) {
        showWarningMessage = true;
        warningMessageType = WARNING_MESSAGE_TYPE_MOVE_IN_DATE_TOO_FAR;
      }
      else if (showWarningMoveInDateBeforeHomeAvailable({
        moveInDate: currentUser.searchFilters.moveInDate,
        listingAvailableDate: listing.availableDate,
      })) {
        showWarningMessage = true;
        warningMessageType = WARNING_MESSAGE_TYPE_MOVE_IN_DATE_BEFORE_AVAILABLE;
      }
    }

    const isQualified = listing && renterProfile
      ? isRenterQualifiedForListing(renterProfile, listing, tenantScreeningCriteria)
      : true;

    const isPrequalCompleted = !!(renterProfile && renterProfile.isPrequalCompleted);
    const hideApplicationButtonLimitedTours = (
      listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM
      && isLimitedTours
      && !queryUrl.apply
    );

    const listingAvailableDateRange = getListingAvailableDateRange({ listing });
    const listingAvailableDateRangeText = [
      momentWithAustinTimezone(listingAvailableDateRange.startDate).format('MMM D'),
      momentWithAustinTimezone(listingAvailableDateRange.endDate).format('MMM D'),
    ].join(' - ');

    const listingContactPhone = getListingContactPhone({
      currentUser,
      renterProfilesByUserId,
      listing,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      assignedApartmentLocator,
    });

    const noPetsPrequalDisabled = (
      disabledSettings.includes('noPets')
      && listing.dataSource !== LISTING_DATA_SOURCE_SUNROOM
    );

    const concession = getLatestActiveConcession(concessions);

    const preLeaseActive = isPreLeaseActive({ listing });

    let touringOptionsDOM = null;
    if (
      !viewingListing
      && !adminTourId
      && !isSmartApartmentData
    ) {
      if (isSelfShowing) {
        touringOptionsDOM = (
          <SrListingSelfTouringSection
            redirectToApartmentFlow={apartment && apartment.selfTourFlowEnabled}
            listing={listing}
            dispatch={dispatch}
            currentUser={currentUser}
            history={history}
          />
        );
      }
      else if (preLease && preLeaseActive) {
        touringOptionsDOM = (
          <SrListingJoinWaitlist
            listing={listing}
            preLease={preLease}
          />
        );
      }
      else {
        touringOptionsDOM = (
          <SrListingTourSection
            requestedFrom={requestedFrom}
            listing={showUnit || listing}
            tourStartDate={listingTour && listingTour.startDate}
            tours={tours}
            partner={partner}
            latestTourRequest={buyersSideTourRequests[`${listing.id}-${currentUser.id}`]}
          />
        );
      }
    }

    return (
      <div className='sectionContainer'>
        <div
          id='hero'
          style={isTransitioning ? {} : { backgroundImage: `url("${managedPhotoUrls[0]}")` }}
          onClick={this._onClickPhoto}
        >
          <Swipeable className='swipeable-wrapper' onSwiped={this._onSwipedPhoto}>
            <div
              onClick={this._onBookmarkClick}
              className={classnames(
                'listingBookmarkIcon',
                isBookmarked && 'active',
              )}
            />
            <span
              id='photoCount'
              onClick={() => history.push(`/listing/${listing.id}/photos`)}
            >
              {managedPhotoUrls.length} Photos
            </span>
            <div className='listingTags'>
              {isSelfShowingListing(listing) && (
                <SrIcon className='label-self-tour-bolt-yellow' />
              )}
              {media3DTours.length > 0 && (
                <SrIcon className='label-3d-tour-orange' />
              )}
            </div>
          </Swipeable>
        </div>

        <div className='section mainInfo'>
          <SrListingPriceBlock
            listing={listing}
            concession={concession}
            marketingListing={marketingListingsByListingId[listing.id]}
            preLease={preLease}
          />
        </div>

        {concession && (
          <SrBox marginTop='32px' className='section'>
            <SrConcessionsBanner
              concession={concession}
              rentPrice={listing.price}
            />
          </SrBox>
        )}

        {!isSmartApartmentData && listing.description && (
          <div className='section titled oneColumn'>
            <span className='title'>description</span>
            <div className='contentContainer'>
              <span style={{ whiteSpace: 'pre-line' }}>
                {listing.description}
              </span>
            </div>
          </div>
        )}

        {media3DTours.length > 0 && (
          <SrBox paddingTop='42px'>
            <SrBox paddingLeft='33px'>
              <SrText weight='bold' font='whitney-sc'>3d tour</SrText>
            </SrBox>
            <SrBox marginTop='17px'>
              <SrMediaCarousel
                padding='0 33px'
                mediaList={media3DTours}
                type={MEDIA_TYPE_3D_TOUR}
                onMediaOpen={this._on3dTourOpen}
                onMediaClose={this._on3dTourClose}
              />
            </SrBox>
          </SrBox>
        )}
        {mediaVideos.length > 0 && (
          <SrBox paddingTop='42px'>
            <SrBox paddingLeft='33px'>
              <SrText weight='bold' font='whitney-sc'>video</SrText>
            </SrBox>
            <SrBox marginTop='17px'>
              <SrMediaCarousel
                padding='0 33px'
                mediaList={mediaVideos}
                type={MEDIA_TYPE_VIDEO}
              />
            </SrBox>
          </SrBox>
        )}

        {apartmentSpecialDeals && apartmentSpecialDeals.length > 0 && (
          <div className='section titled'>
            <SrApartmentSpecialDealsList
              apartmentSpecialDeals={apartmentSpecialDeals}
              apartmentUnit={listing.apartmentUnit}
              floorPlanId={listing.floorPlanId}
            />
          </div>
        )}

        {listingHowDoILeaseSectionV1 === 'on'
          && !isLimitedTours
          && !noPetsPrequalDisabled
          && listing.status === LISTING_STATUS_ACTIVE && (
          <div className='section'>
            <SrHowDoILeaseListingSection
              listingId={listing.id}
              isSelfShowing={isSelfShowing}
              onPrequalClick={this._goToPrequalSection}
              onRequestTourClick={this._goToScheduleTourSection}
              onUnlockDoorClick={this._goToScheduleTourSection}
              marketName={market && market.displayName || null}
            />
          </div>
        )}

        {!adminTourId && !noPetsPrequalDisabled && isSunroomListing && (
          <div className='section titled' id='scrollHereForPrequal' ref={this._onListingPrequalSectionRef}>
            <SrListingPrequal
              listing={listing}
              currentUser={currentUser}
              renterProfile={renterProfile}
              tenantScreeningCriteria={tenantScreeningCriteria}
              history={history}
              onClickRequestTour={this._goToScheduleTourSection}
              dispatch={dispatch}
              location={location}
              householdsApplyingToListing={householdsApplyingToListingByListingId[listing.id]}
            />
          </div>
        )}
        {!adminTourId && noPetsPrequalDisabled && isSunroomListing && (
          <div className='section titled' id='scrollHereForPrequal' ref={this._onListingPrequalSectionRef}>
            <SrApplicationButton
              listing={listing}
            />
          </div>
        )}
        {showMoveInWindowSection && (
          <div className='section titled'>
            <SrListingMoveInWindow
              dispatch={dispatch}
              listing={listing}
              currentUser={currentUser}
              tenantScreeningCriteria={tenantScreeningCriteria}
            />
          </div>
        )}

        <SrOpenStreetMap
          coordinates={[listing.locationLongitude, listing.locationLatitude]}
          initialZoom={12}
          onMapClick={() => {
            history.push(`/listing/${listing.id}/map`);
          }}
        />

        {isAgent && listingShowingInfo && listingShowingInfo.notesForShowing && (
          <div className='section titled twoColumns'>
            <span className='title'>notes for tour guide</span>
            <div className='contentContainer'>
              <div className='row'>
                <span>
                  {listingShowingInfo.notesForShowing}
                </span>
              </div>
            </div>
          </div>
        )}

        {isAgent && listingShowingInfo && listingShowingInfo.lockboxCode && (
          <div className='section titled twoColumns'>
            <span className='title'>combo code</span>
            <div className='contentContainer'>
              <div className='row'>
                <span>
                  {listingShowingInfo.lockboxCode} (Location is "{listingShowingInfo.lockboxLocation}")
                </span>
              </div>
            </div>
          </div>
        )}

        {isAgent && listingShowingInfo && !isSmartApartmentData && (
          <div className='section titled twoColumns'>
            <span className='title'>how to show</span>
            <div className='contentContainer'>
              <div className='row'>
                <span className='left'>Instructions</span>
                <span className='right'>{listingShowingInfo.showingInstructions
                  ? listingShowingInfo.showingInstructions
                  : '--'}
                </span>
              </div>
              <div className='row'>
                <span className='left'>Access</span>
                <span className='right'>{listingShowingInfo.showingAccessInstructions
                  ? listingShowingInfo.showingAccessInstructions.join(', ')
                  : '--'}
                </span>
              </div>
              <div className='row'>
                <span className='left'>Occupant Type</span>
                <span className='right'>{listingShowingInfo.occupantType
                  ? listingShowingInfo.occupantType
                  : '--'}
                </span>
              </div>
              <div className='row'>
                <span className='left'>Lockbox Type</span>
                <span className='right'>{listingShowingInfo.lockboxType === LISTING_LOCKBOX_TYPE_LOCKBOX
                  ? 'Supra Lockbox'
                  : listingShowingInfo.lockboxType}
                </span>
              </div>
              <div className='row'>
                <span className='left'>Lockbox Location</span>
                <span className='right'>{listingShowingInfo.lockboxLocation
                  ? listingShowingInfo.lockboxLocation
                  : '--'}
                </span>
              </div>
              <div className='row'>
                <span className='left'>MLS #</span>
                <span className='right'>{listing.mlsNumber
                  ? listing.mlsNumber
                  : '--'}
                </span>
              </div>
            </div>
          </div>
        )}

        {isAgent && listingShowingInfo && listingShowingInfo.showingRemarks && (
          <div className='section titled twoColumns'>
            <span className='title'>
              private remarks
            </span>
            <div className='contentContainer'>
              <div className='row'>
                <span>
                  {listingShowingInfo.showingRemarks}
                </span>
              </div>
            </div>
          </div>
        )}

        {isAgent && (
          <div className='section titled twoColumns'>
            <span className='title'>general info</span>
            <div className='contentContainer'>
              {listing.apartmentPhone && (
                <div className='row'>
                  <span className='left'>Apartment Office Phone</span>
                  <span className='right'>{formatPhoneNumber(listing.apartmentPhone)}</span>
                </div>
              )}
              <div className='row'>
                <span className='left'>Washer / Dryer</span>
                <span className='right'>{listing.hasWasherDryer ? 'Yes' : 'No'}</span>
              </div>
              <div className='row'>
                <span className='left'>Washer / Dryer Connections</span>
                <span className='right'>{listing.hasWasherDryerConnections ? 'Yes' : 'No'}</span>
              </div>
              <div className='row'>
                <span className='left'>Year Built</span>
                <span className='right'>{listing.yearBuilt}</span>
              </div>
            </div>
          </div>
        )}

        {shouldShowSchoolInformation && (
          <div className='section titled twoColumns'>
            <span className='title'>schools</span>
            <div className='contentContainer'>
              <div className='row'>
                <span className='left'>Elementary School</span>
                <span className='right'>{listing.schoolElementary || 'N/A'}</span>
              </div>
              <div className='row'>
                <span className='left'>Middle School</span>
                <span className='right'>{listing.schoolMiddle || 'N/A'}</span>
              </div>
              <div className='row'>
                <span className='left'>Senior High School</span>
                <span className='right'>{listing.schoolSeniorHigh || 'N/A'}</span>
              </div>
              <div className='row'>
                <span className='left'>School District</span>
                <span className='right'>{listing.schoolDistrict || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {propertyFeatures && propertyFeatures.length > 0 && (
          <div className='section titled twoColumns'>
            <span className='title'>property features</span>

            <SrBox marginTop={17}>
              <SrPropertyFeaturesBlock
                propertyFeatures={propertyFeatures}
              />
            </SrBox>
          </div>
        )}

        {isSunroomListing && (
          <div className='section titled twoColumns'>
            <span className='title'>expenses</span>

            <SrBox marginTop={17} marginBottom={3}>
              <SrListingExpensesCard
                residentBenefitsPackageAmount={partner?.residentBenefitsPackageAmount}
                listing={listing}
                marketsById={marketsById}
                proratedRentFirstMonth={partner?.experienceConfiguration?.proratedRentFirstMonth}
              />
            </SrBox>
          </div>
        )}

        {listing.displayType !== LISTING_DISPLAY_TYPE_MULTI_UNIT
          && (listing.petsAllowed || listing.petsNegotiable) && (
          <div className='section titled twoColumns'>
            <span className='title'>pet policy</span>
            <SrBox className='SrFeatureCardGrid' marginTop={17} marginBottom={3}>
              {(listing.petsNegotiable || isBoolean(listing.catsAllowed)) && (
                <SrFeatureCard
                  emoji={listing.petsNegotiable || listing.catsAllowed ? '😺' : '😺🚫'}
                  subtitle={listing.petsNegotiable ? 'cats\n(negotiable)' : (
                    listing.catsAllowed ? 'cat friendly' : 'no cats'
                  )}
                />
              )}
              {(listing.petsNegotiable || isBoolean(listing.dogsAllowed)) && (
                <SrFeatureCard
                  emoji={listing.petsNegotiable || listing.dogsAllowed ? '🐶' : '🐶🚫'}
                  subtitle={listing.petsNegotiable ? 'dogs\n(negotiable)' : (
                    listing.dogsAllowed ? 'dog friendly' : 'no dogs'
                  )}
                />
              )}
              {typeof listing.maximumPetsAllowed === 'number' && (
                <SrFeatureCard
                  title={`${listing.maximumPetsAllowed}`}
                  subtitle={listing.petsNegotiable
                    ? 'max pets\n(negotiable)'
                    : 'max pets'
                  }
                />
              )}
              {!shouldDisableRealSavvyFields && (
                <SrFeatureCard
                  title={petWeightLimit && hasListingPetWeightLimit(petWeightLimit)
                    ? (
                      <SrText size='inherit' weight='inherit'>
                        {petWeightLimit}
                        <SrText size='2xl' weight='bold'>lb</SrText>
                      </SrText>
                    )
                    : '🐾'
                  }
                  subtitle={
                    petWeightLimit && hasListingPetWeightLimit(petWeightLimit)
                      ? (!listing.petsNegotiable ? 'weight limit' : 'weight limit\n(negotiable)')
                      : 'no weight limit'
                  }
                />
              )}
            </SrBox>
            {tenantScreeningCriteria?.breedRestrictions.length > 0 && (
              <SrBox marginTop={15}>
                <SrFeatureCard
                  title={`${tenantScreeningCriteria.breedRestrictions.length} 🚫`}
                  subtitle={listing.petsNegotiable
                    ? 'dog breeds not allowed\n(negotiable)'
                    : 'dog breeds not allowed'}
                  ctaLabel='view all'
                  ctaOnClick={this._onToggleBreedRestrictionsModal}
                />
              </SrBox>
            )}
          </div>
        )}

        {showBreedRestrictionsModal && (
          <SrDarkModal onClose={this._onToggleBreedRestrictionsModal}>
            <SrModalContent>
              <div className='t-centered'>
                <SrText weight='bold' size='2xl'>
                  Restricted Breeds
                </SrText>
                {tenantScreeningCriteria?.breedRestrictions.map(({ name }) => (
                  <SrBox marginTop={15} key={name}>
                    <SrText size='large'>
                      {name}
                    </SrText>
                  </SrBox>
                ))}
              </div>
            </SrModalContent>
          </SrDarkModal>
        )}

        {isSmartApartmentData && existsPetData(listing) && (
          <div className='section titled twoColumns'>
            <span className='title'>pets</span>
            <SrBox className='SrFeatureCardGrid' marginTop={17} marginBottom={3}>
              {listing.maximumPetsAllowed !== null && (
                <SrFeatureCard
                  title={`${listing.maximumPetsAllowed}`}
                  subtitle='max pets'
                />
              )}
              {listing.petWeight !== null && (
                <SrFeatureCard
                  title={listing.petWeight && hasListingPetWeightLimit(listing.petWeight)
                    ? (
                      <SrText size='inherit' weight='inherit'>
                        {listing.petWeight}<SrText size='2xl' weight='bold'>lb</SrText>
                      </SrText>
                    )
                    : <SrText size='2xl' weight='bold'>No Weight Limit</SrText>
                  }
                  subtitle='weight limit'
                />
              )}
            </SrBox>
          </div>
        )}

        {!isSmartApartmentData && (
          <SrBox marginBottom={3} className='section titled twoColumns'>
            <span className='title'>general info</span>
            <SrBox className='SrFeatureCardList' marginTop={15}>
              {listing.listedDate && (
                <SrFeatureCard
                  type='row'
                  title={momentWithAustinTimezone(listing.listedDate).toNow(true).replace('a ', '1 ')}
                  subtitle='time on sunroom'
                  emoji='🕒'
                />
              )}
              {listing.yearRenovated && (
                <SrFeatureCard
                  type='row'
                  title={listing.yearRenovated}
                  subtitle='year renovated'
                  emoji='✨'
                />
              )}
              <SrFeatureCard
                type='row'
                title={listing.yearBuilt ? listing.yearBuilt : 'N/A'}
                subtitle='year built'
                emoji='🛠'
              />
              {!shouldDisableRealSavvyFields && !disabledSettings.includes('leaseLength') && (
                <SrFeatureCard
                  type='row'
                  title={leaseLengthValue}
                  subtitle={leaseLengthLabel}
                  emoji='📄'
                />
              )}
              {listing.availableDate && !isSunroomListing && (
                <SrFeatureCard
                  type='row'
                  title={(
                    <SrText>
                      <SrText size='2xl' weight='bold'>{`${listingAvailableDateRangeText} `}</SrText>
                      {showWarningMessage
                        && warningMessageType === WARNING_MESSAGE_TYPE_MOVE_IN_DATE_TOO_FAR
                        && (
                          <SrTooltip
                            icon={<SrIcon className='question-mark-circle-small' />}
                            bgColor='blue'
                            className='warning-move-in'
                          >
                            {
                              'Since the home is available on ' +
                              `${moment.utc(listing.availableDate).format('MMMM Do')}, ` +
                              'eligible renters must be able to pay rent ' +
                              ` by ${moment.utc(listing.availableDate).add(15, 'days').format('MMMM Do')}. `
                            }
                            <br />
                            <br />
                            <span
                              onClick={this._openUpdateMoveInDateModal}
                              className='blue-bright cursor-pointer'
                            >
                              Click here
                            </span>
                            {
                              ' to update your move in date' +
                              ' or go to our '
                            }
                            <span>
                              <SrLink
                                to='/faq-move-in-date'
                              >
                                <span
                                  className='updateMoveInDateLink'
                                >
                                  FAQ to learn more.
                                </span>
                              </SrLink>
                            </span>
                          </SrTooltip>
                        )}
                      {showWarningMessage
                        && warningMessageType === WARNING_MESSAGE_TYPE_MOVE_IN_DATE_BEFORE_AVAILABLE
                        && (
                          <SrTooltip
                            icon={<SrIcon className='question-mark-circle-small' />}
                            bgColor='blue'
                            className='warning-move-in'
                          >
                            {
                              'This home is not available until ' +
                              `${moment.utc(listing.availableDate).format('MMMM Do')} ` +
                              'after your move in date of ' +
                              `${moment.utc(currentUser.moveInDate).format('MMMM Do')}. `
                            }
                            <br />
                            <br />
                            <span
                              onClick={this._openUpdateMoveInDateModal}
                              className='blue-bright cursor-pointer'
                            >
                              Click here
                            </span>
                            {
                              ' to update your move in date' +
                              ' or go to our '
                            }
                            <span>
                              <SrLink
                                to='/faq-move-in-date'
                              >
                                <span
                                  className='updateMoveInDateLink'
                                >
                                  FAQ to learn more.
                                </span>
                              </SrLink>
                            </span>
                          </SrTooltip>
                        )}
                      {showWarningMessage
                        && warningMessageType === WARNING_MESSAGE_TYPE_MOVE_IN_DATE_IN_PAST
                        && (
                          <SrTooltip
                            icon={<SrIcon className='warning-icon' />}
                            bgColor='blue'
                            className='warning-move-in'
                          >
                            {
                              'This home has been available since ' +
                              `${moment.utc(listing.availableDate).format('MMMM Do')}, ` +
                              'so eligible renters must be able to pay rent by ' +
                              `${momentWithAustinTimezone().add(15, 'days').format('MMMM Do')}. `
                            }
                            <br />
                            <br />
                            <span
                              onClick={this._openUpdateMoveInDateModal}
                              className='blue-bright cursor-pointer'
                            >
                              Click here
                            </span>
                            {
                              ' to update your move in date' +
                              ' or go to our '
                            }
                            <span>
                              <SrLink
                                to='/faq-move-in-date'
                              >
                                <span
                                  className='updateMoveInDateLink'
                                >
                                  FAQ to learn more.
                                </span>
                              </SrLink>
                            </span>
                          </SrTooltip>
                        )}
                    </SrText>
                  )}
                  subtitle='available for move-in'
                  emoji='🗓'
                />
              )}
            </SrBox>
          </SrBox>
        )}

        {isSmartApartmentData && (
          <div className='section titled twoColumns'>
            <span className='title'>general info</span>
            <SrBox className='SrFeatureCardList' marginTop={15}>
              {listing.yearRenovated && (
                <SrFeatureCard
                  type='row'
                  title={listing.yearRenovated}
                  subtitle='year renovated'
                  emoji='✨'
                />
              )}
              <SrFeatureCard
                type='row'
                title={listing.yearBuilt ? listing.yearBuilt : 'N/A'}
                subtitle='year built'
                emoji='🛠'
              />
              {listing.coSigners && (
                <SrFeatureCard
                  type='row'
                  title='Accepted'
                  subtitle='guarantors policy'
                  emoji='👩👨'
                />
              )}
              {listing.numberOfUnits && (
                <SrFeatureCard
                  type='row'
                  title={listing.numberOfUnits}
                  subtitle='number of units'
                  emoji='🏢'
                />
              )}
            </SrBox>
          </div>
        )}

        {Object.keys(getOwnerPaysForFields(listing)).length > 0 && (
          <div className='section titled oneColumn'>
            <span className='title'>who covers the bills</span>
            <div className='contentContainer'>
              <SrWhoCoversTheBillsInfoBlock property={listing} />
            </div>
          </div>
        )}

        {activeListingAmenities.length > 0 && (
          <div className='section titled twoColumns'>
            <span className='title'>amenities</span>
            <SrBox className='SrFeatureCardGrid' marginTop={17}>
              {activeListingAmenities.map((amenity) => (
                <SrFeatureCard
                  key={amenity.title}
                  emoji={amenity.emoji}
                  subtitle={amenity.title}
                  iconClassName={amenity.iconClassName}
                  className='isAmenity'
                />
              ))}
            </SrBox>
          </div>
        )}

        {isAgent && !isSmartApartmentData && (
          <div className='section titled twoColumns'>
            <span className='title'>listing agent</span>
            <div className='contentContainer'>
              <div className='row'>
                <span className='left'>Name</span>
                <span className='right'>{listing.listingAgent
                  ? listing.listingAgent
                  : '--'}
                </span>
              </div>
              <div className='row'>
                <span className='left'>Cell Phone</span>
                <span className='right'>{listing.listingAgentCellPhone
                  ? (
                    <a className='blue-bright' href={`tel:+1${listing.listingAgentCellPhone}`}>
                      {formatPhoneNumber(listing.listingAgentCellPhone)}
                    </a>
                  )
                  : '--'}
                </span>
              </div>
              <div className='row'>
                <span className='left'>Work Phone</span>
                <span className='right'>{listing.listingAgentDirectWorkPhone
                  ? (
                    <a className='blue-bright' href={`tel:+1${listing.listingAgentDirectWorkPhone}`}>
                      {formatPhoneNumber(listing.listingAgentDirectWorkPhone)}
                    </a>
                  )
                  : '--'}
                </span>
              </div>
            </div>
          </div>
        )}

        {listingAdminInfoById[listing.id] && (
          <SrListingAdminList
            listing={listing}
            marketingListing={marketingListingsByListingId[listing.id]}
            market={market}
            partner={partner}
            partnerUsers={partnerUserListByPartnerId[
              (partner || {}).id
            ] || []}
            lease={leasesByListingId[listing.id]}
            apartment={apartment}
            sunlock={sunlocksById[listing.sunlockId]}
            listingLinks={listingLinksByListingId[listing.id] || []}
            listingAdminInfo={listingAdminInfoById[listing.id]}
            redirection={getRedirectionByListingId[listing.id]}
          />
        )}

        {apartment && (
          <div className='section noTitle apartmentLink'>
            <SrLinkCard
              title='the apartment'
              label={apartment.name}
              linkTo={`/apartment/${apartment.id}`}
              backgroundImageUrl={apartment.photoUrls[0]}
            />
          </div>
        )}

        {isSmartApartmentData && (
          <div className='section titled twoColumns table'>
            <span className='title'>
              {`${pluralize('unit layout', listing.apartmentUnits.length, true)} in the building`}
              {!viewAllApartmentUnits && (
                <span
                  className='viewAll'
                  onClick={this._onViewAllApartmentUnitsClick}
                >
                  View all
                </span>
              )}
            </span>
            <div className='contentContainer'>
              <div
                id='scrollHereForScheduleTour'
                className='row threeColumns'
                ref={this._onScheduleTourSectionRef}
              >
                <table>
                  <thead>
                    <tr>
                      <th>layout</th>
                      <th className='right'>sqft</th>
                    </tr>
                  </thead>
                  {(viewAllApartmentUnits
                    ? listing.apartmentUnits
                    : (
                      filterApartmentUnits({
                        apartmentUnits: listing.apartmentUnits,
                        searchFilters: currentUser.searchFilters,
                      })
                    )
                  ).map((apartmentUnit) => {
                    return (
                      <SrApartmentUnitRow
                        key={apartmentUnit.smartApartmentUnitId}
                        apartmentUnit={apartmentUnit}
                      />
                    );
                  })}
                </table>
              </div>
            </div>
          </div>
        )}

        {listing.displayType === LISTING_DISPLAY_TYPE_SINGLE_UNIT && (
          <SrListingMoreUnits listing={listing} />
        )}

        <div
          className='section oneColumn titled scheduleTour'
          id='scrollHereForScheduleTour'
          ref={this._onScheduleTourSectionRef}
        >
          {!viewingListing
            && !adminTourId
            && !isSmartApartmentData
            && (
              <div className='sectionInnerContainer'>
                {listing.cantTourFlag && !showUnit && !isRealSavvy
                  ? (
                    <div>
                      <span className='title'>
                        apply online today
                      </span>
                      <div className='contentContainer'>
                        <span>
                          {'This specific unit is available to be leased, but not toured. '}
                        </span>
                        <div style={{ paddingTop: '17px' }}>
                          {!hideApplicationButtonLimitedTours && isQualified && (
                            <SrApplicationButton
                              listing={listing}
                            />
                          )}
                          {isPrequalCompleted && !isQualified && (
                            <div>
                              <ButtonDeprecated theme={BUTTON_THEME_YELLOW} disabled>
                                not qualified to apply
                              </ButtonDeprecated>
                              <div className='t-centered'>
                                <div style={{ height: 8 }} />
                                <SrText weight='medium' size='medium'>
                                  You must match all qualifications to apply.
                                </SrText>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                  : touringOptionsDOM
                }
              </div>
            )
          }
          {isAgent && agentTourId && (
            <div ref={this._onTourAgentSectionRef}>
              <SrListingTourAgentSection
                listing={listing}
                agentTourId={agentTourId}
                history={history}
                onClickScrollDownButton={this._goToTourAgentSection}
              />
            </div>
          )}

          {isAdmin && adminTourId && (
            <SrListingAddToTourSection
              tourId={adminTourId}
              listingId={listing.id}
            />
          )}
        </div>
        {partner
          && partner.billingPlan !== PARTNER_BILLING_PLAN_FULL_SERVICE
          && listing.apartmentPhone
          && listing.apartmentEmail
          && (
            <div className='section'>
              <br />
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE}
                onClick={this._onClickApartmentPhoneCall}
              >
                <div className='call-button'>
                  <SrIcon className='phone' />
                  call
                </div>
              </ButtonDeprecated>
              {currentUser.email && (
                <>
                  <br />
                  <ButtonDeprecated
                    theme={BUTTON_THEME_WHITE}
                    onClick={this._openAskQuestionModal}
                  >
                    ask question
                  </ButtonDeprecated>
                </>
              )}
            </div>
          )}
        <div className='section recommendedHomes'>
          <SrRecommendedListings
            listingPhotoResolution={listingPhotoResolution}
            listings={recommenedListingsRecords}
            isLoading={recommendedListingApiState.inProgress}
            isFullyLoaded={isRecommendedListingsFullyLoaded}
            screenId={SCREEN_ID_LISTING}
          />
        </div>
        {!isAgent && (
          <div className='footer'>
            <span className='broker'>
              {isSmartApartmentData ? '' : `Property listed by ${listing.listingBroker}`}
            </span>
          </div>
        )}
        {showContactModal &&
          <SrContactModal
            isOpen={showContactModal}
            onClose={this._onCloseContactModal}
            onClickMessage={this._onClickMessage}
            onClickCall={this._onClickCall}
            url={url.resolve(appUrl, getListingUrlPath(listing))}
            phoneNumber={listingContactPhone}
          />
        }
        {showAskQuestionModal &&
          <SrAskQuestionModal
            listingId={listing.id}
            userEmail={currentUser.email}
            onClose={this._onCloseAskQuestionModal}
            dispatch={dispatch}
          />
        }
        {showPartnerTourRequestNewModal && (
          <SrSimpleMessageModal
            className='newPartnerTourRequest'
            iconUrl='/images/house-illustration2x.png'
            headline={
              <span>
                The property manager <br /> will contact you soon!
              </span>
            }
            message={
              <span>
                This property does their own leasing. So Sunroom has sent the property
                manager your tour request and contact info.
                <SrBox marginTop='9px'>
                  <SrText weight='600' display='block'>
                    Look for a call, text, or email from:
                  </SrText>
                  <SrText weight='600' display='block'>
                    {listing.listingManagementCompany}
                  </SrText>
                </SrBox>
              </span>
            }
            buttonText='ok thanks!'
            maxWidth={335}
            maxIconWidth={150}
            onButtonClick={this._onClosePartnerTourRequestNewModal}
          />
        )}
        {showPartnerTourRequestFollowUpModal && (
          <SrSimpleMessageModal
            className='newPartnerTourRequest'
            iconUrl='/images/house-illustration2x.png'
            headline={
              <span>
                Thank you for <br /> responding!
              </span>
            }
            message={
              <span>
                This property does their own leasing. So Sunroom takes
                this survey to keep them accountable.
                <SrBox marginTop='9px'>
                  <SrText weight='600' display='block'>
                    If they did not respond, we will let
                    them know so they contact you!
                  </SrText>
                </SrBox>
              </span>
            }
            buttonText='ok thanks!'
            maxWidth={335}
            maxIconWidth={150}
            onButtonClick={this._onClosePartnerTourRequestSurveyModal}
          />
        )}
      </div>
    );
  }

  _getTenantScreeningRedirectUrl = () => {
    const { appUrl, listing, currentPartner, currentUser } = this.props;
    const { roles } = currentUser;

    if (hasRolePartner(roles)) {
      if (currentPartner.id === listing.partnerId) {
        return `/partner/${currentPartner.slug}/listing/${listing.id}`;
      }

      return `/partner/${currentPartner.slug}`;
    }
    if (hasRoleRenter(roles)) {
      return '/profile';
    }
    if (hasRoleAnonymous(roles)) {
      return getListingApplyURL(listing, appUrl);
    }

    // For whatever role that is not handled above, redirect to the feed.
    return '/feed';
  }

  render() {
    const {
      listing,
      currentUser,
      assignedApartmentLocator,
      listingApiState,
      appUrl,
      location,
      isTransitioning,
      renterProfilesByUserId,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      marketsById,
    } = this.props;
    const {
      showBookmarkToast,
    } = this.state;

    if (listing && !hasRoleAdmin(currentUser.roles) && isListingTenantScreening(listing)) {
      return <Redirect to={this._getTenantScreeningRedirectUrl()} />;
    }

    const market = listing && marketsById[listing.marketId] || null;
    const isAgent = hasRoleAgent(currentUser.roles);
    const isApartmentLocator = hasRoleApartmentLocator(currentUser.roles);
    const sunroomListingLink = listing && url.resolve(appUrl, getListingUrlPath(listing));
    const queryUrl = queryString.parse(location.search);
    const leadFirstName = queryUrl.leadName ? `${queryUrl.leadName.split(' ')[0].toLowerCase()}` : null;
    const leadUserId = queryUrl.leadUserId ? queryUrl.leadUserId : null;
    const activityType = queryUrl.activityType ? queryUrl.activityType : null;
    const activityStr = activityType === LEAD_ACTIVITY_BOOKMARK
      ? 'saved by'
      : (
        activityType === LEAD_ACTIVITY_CHECK_AVAILABILITY
          ? 'check availability by'
          : null
      );

    const listingContactPhone = getListingContactPhone({
      currentUser,
      renterProfilesByUserId,
      listing,
      tenantScreeningCriteriaByPartner,
      tenantScreeningCriteriaByListingId,
      assignedApartmentLocator,
    });

    const navbarOptions = {
      leftButton: (
        <SrListingBackButton
          isTransitioning={isTransitioning}
        />
      ),
      rightButton: (
        <SrNavbarToastButton
          toastLink={sunroomListingLink}
          toastContent='share link copied!'
          onClick={this._onClickShareButtonLink}
          buttonContent={(
            <img
              className='share listing'
              alt='share listing'
              src='/images/share-icon.svg'
            />
          )}
          toggleToast
        />
      ),
      secondaryRightButton: listingContactPhone && (
        <NavbarButton
          onClick={this._onPhoneClicked}
        >
          <img
            className='phone listing'
            alt='phone listing'
            src='/images/phone-icon.svg'
          />
        </NavbarButton>
      ),
    };

    if (isAgent) {
      navbarOptions.redirectLogoTo = '/agent/tours';
    }

    if (showBookmarkToast) {
      navbarOptions.navbarToast = (
        <SrNavbarToast
          toastContent='❤️ saved home!'
        />
      );
    }

    if (listing
      && listing.dataSource === LISTING_DATA_SOURCE_SUNROOM
      && isAgent
      && queryUrl.agentTourId) {
      navbarOptions.navbarToast = (
        <SrNavbarToast
          toastContent='sunroom listing - write good feedback!'
          theme={SR_NAVBAR_TOAST_THEME_YELLOW}
          disappear={false}
        />
      );
    }

    if (listing?.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT) {
      return (
        <SrSimpleMessageModal
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          iconUrl='/images/sunroom-sad.svg'
          headline={
            'We\'re sorry! This listing is not part of the Sunroom network and is no longer available.'
          }
          message={
            <SrText color='grey' font='greycliff' weight='600'>
              On the other hand we have thousands of listings in your area!
              Browse the best rentals {market?.displayName || 'Sunroom'} has to offer and find your new home!
            </SrText>
          }
          buttonText='Browse the Best Rentals'
          buttonTheme={BUTTON_THEME_GREEN_ADOBE}
          buttonHref='/feed'
          maxWidth={375}
          maxIconWidth={150}
          underline
        />
      );
    }

    return (
      <div>
        <div className='page-listing'>
          <Navbar {...navbarOptions} />
          {isApartmentLocator && activityStr && (
            <SrActivityBanner
              activityStr={activityStr}
              leadFirstName={leadFirstName}
              leadUserId={leadUserId}
            />
          )}
          {listingApiState.inProgress
            ? <ActivityIndicator />
            : (listing
              ? this.renderListing()
              : null
            )
          }
        </div>
      </div>
    );
  }
}

ListingPage.displayName = 'ListingPage';

export default ListingPage;
